import type { CroppedRegion } from "@/interfaces/estimate-detail";
import type { PdfPageAsImage } from "@/interfaces/estimation.interface";
import { estimationSelector } from "@/redux/selectors/estimation.selector";
import { useSelector } from "@/redux/store";
import { generateCanvasAndDrawRegions } from "@/utils/utils";
import { useEffect, useMemo, useRef, useState } from "react";
import { PDFPreviewEmptyState } from "./PDFPreviewEmptyState";
import { ZoomableImage, type ZoomableImageRef } from "@/components/ZoomableImage";

type PageWithImage = PdfPageAsImage & { imagePath: string };

// Delay before navigating to highlight, allows images to render
const NAVIGATION_DELAY_MS = 150;

export const PdfPagesWithHighlights = ({
  pdfPagesAsImages: rawPages,
  showWithoutLineItem = false,
  fillContainer = false,
  showOnlyHighlightedPage = false,
}: {
  pdfPagesAsImages: PdfPageAsImage[];
  showWithoutLineItem?: boolean;
  fillContainer?: boolean;
  showOnlyHighlightedPage?: boolean;
}) => {
  const zoomableRef = useRef<ZoomableImageRef>(null);
  const highlightedPageRef = useRef<HTMLDivElement>(null);
  const prevPagesRef = useRef<PdfPageAsImage[]>([]);

  const [baseImagesMap, setBaseImages] = useState<Map<string, PageWithImage>>(new Map());
  const [displayImages, setDisplayImages] = useState<PageWithImage[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const lineItemDetail = useSelector(estimationSelector.lineItemDetail);
  const mappings = lineItemDetail?.lineItem?.croppedImageMappingsList;
  const lineItemKey = lineItemDetail?.lineItem?.lineItem ?? "";

  const pages = useMemo(() => [...rawPages].sort((a, b) => a.pageNumber - b.pageNumber), [rawPages]);
  const pdfKey = pages[0]?.remotePath ?? "";

  const regionsByPath = useMemo(() => {
    if (!mappings?.length) return {};
    return mappings.reduce(
      (acc, m) => {
        (acc[m.imagePath] ??= []).push(m.region);
        return acc;
      },
      {} as Record<string, CroppedRegion[]>,
    );
  }, [mappings]);

  const { highlightedPage, highlightRegion } = useMemo(() => {
    const page = pages.find((p) => regionsByPath[p.remotePath]?.length > 0);
    const region = page ? regionsByPath[page.remotePath]?.[0] : null;
    return { highlightedPage: page, highlightRegion: region };
  }, [pages, regionsByPath]);

  // Load base images when pages change
  useEffect(() => {
    const hasPagesChanged = pages.length !== prevPagesRef.current.length || pages[0]?.remotePath !== prevPagesRef.current[0]?.remotePath;

    if (!hasPagesChanged && baseImagesMap.size > 0) return;
    prevPagesRef.current = pages;

    let cancelled = false;

    const loadImages = async () => {
      setIsLoading(true);
      try {
        const results = await Promise.all(
          pages.map(async (page) => {
            const canvas = await generateCanvasAndDrawRegions(page.remotePath, []);
            return { ...page, imagePath: canvas?.toDataURL() ?? "" };
          }),
        );
        if (cancelled) return;
        const valid = results.filter((r) => r.imagePath);
        const map = new Map(valid.map((img) => [img.remotePath, img]));
        setBaseImages(map);
        setDisplayImages(valid);
      } catch (err) {
        if (cancelled) return;
        console.error("Error loading PDF images:", err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadImages();
    return () => {
      cancelled = true;
    };
  }, [pages, baseImagesMap.size]);

  // Apply highlights when line item changes
  useEffect(() => {
    if (baseImagesMap.size === 0) return;

    const pathsToHighlight = Object.keys(regionsByPath);
    if (pathsToHighlight.length === 0) {
      // No highlights - use base images in order
      setDisplayImages(pages.map((p) => baseImagesMap.get(p.remotePath)).filter((img): img is PageWithImage => !!img));
      return;
    }

    let cancelled = false;

    const applyHighlights = async () => {
      // Only re-render pages that need highlights
      const highlighted = await Promise.all(
        pathsToHighlight.map(async (path) => {
          const base = baseImagesMap.get(path);
          if (!base) return null;
          const canvas = await generateCanvasAndDrawRegions(path, regionsByPath[path]);
          return { ...base, imagePath: canvas?.toDataURL() ?? "" };
        }),
      );

      if (cancelled) return;

      const highlightMap = new Map(highlighted.filter((img): img is PageWithImage => !!img).map((img) => [img.remotePath, img]));

      // Merge: use highlighted version if available, otherwise base
      const merged = pages
        .map((p) => highlightMap.get(p.remotePath) ?? baseImagesMap.get(p.remotePath))
        .filter((img): img is PageWithImage => !!img);
      setDisplayImages(merged);
    };

    applyHighlights();
    return () => {
      cancelled = true;
    };
  }, [regionsByPath, baseImagesMap, pages]);

  // Navigate to highlighted region when line item changes
  useEffect(() => {
    if (!displayImages.length || !highlightedPage || !highlightedPageRef.current) return;

    const timer = setTimeout(() => {
      const el = highlightedPageRef.current;
      if (!el) return;

      const img = el.querySelector("img");
      if (!img?.naturalWidth || !img?.naturalHeight || !highlightRegion) {
        // Fallback: scroll to element if we can't get image dimensions or no region
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }

      zoomableRef.current?.zoomToRegion(el, highlightRegion, img.naturalWidth, img.naturalHeight);
    }, NAVIGATION_DELAY_MS);

    return () => clearTimeout(timer);
  }, [displayImages, highlightedPage, highlightRegion, lineItemKey]);

  // Compute which pages to render (filter to single page if requested)
  const pagesToRender = useMemo(() => {
    if (showOnlyHighlightedPage && highlightedPage) {
      return displayImages.filter((img) => img.remotePath === highlightedPage.remotePath);
    }
    return displayImages;
  }, [showOnlyHighlightedPage, highlightedPage, displayImages]);

  // Render conditions
  const isLineItemSelected = !!lineItemDetail;
  const hasMapping = (mappings?.length ?? 0) > 0;

  if (!isLineItemSelected && !showWithoutLineItem) {
    return <div className="hidden" aria-hidden="true" />;
  }

  if (isLineItemSelected && !hasMapping) {
    return <PDFPreviewEmptyState />;
  }

  if (isLoading && !pagesToRender.length) {
    return (
      <div className="m-2 flex h-48 items-center justify-center rounded-md border border-gray-200">
        <div className="flex flex-col items-center gap-2">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-blue-500" />
          <span className="text-sm text-gray-500">Loading PDF pages...</span>
        </div>
      </div>
    );
  }

  if (!pagesToRender.length) {
    return <PDFPreviewEmptyState />;
  }

  const heightClass = fillContainer ? "h-full flex-1" : "max-h-[65vh]";

  return (
    <div className={`relative overflow-hidden rounded-md bg-gray-300 ${fillContainer ? "mx-2 mb-2 flex-1" : "m-2"}`}>
      <ZoomableImage ref={zoomableRef} resetKey={pdfKey} showControls requireModifierForWheelZoom className={`${heightClass} w-full`}>
        <div
          className={`flex ${heightClass} flex-col overflow-y-auto p-6 [&::-webkit-scrollbar]:w-3 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-500 [&::-webkit-scrollbar-track]:bg-gray-200`}
        >
          {pagesToRender.map((page) => (
            <div
              key={page.remotePath}
              ref={page.remotePath === highlightedPage?.remotePath ? highlightedPageRef : undefined}
              className="mb-6 shadow-lg last:mb-0"
            >
              <img src={page.imagePath} alt={`PDF page ${page.pageNumber}`} className="w-full" />
            </div>
          ))}
        </div>
      </ZoomableImage>
    </div>
  );
};
